import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { Breadcrumbs, Button, ListItemButton, TextField, Typography, styled } from '@mui/material'

import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import Dialog from '../components/common/dialog'
import NextIcon from '../assets/icons/next-icon.svg'
import { handleCheckIfOtpCodeIsValid } from '../utils'
import TrashIcon from '../assets/icons/trash-icon.svg'
import { Customer, FormError, Payment } from '../types'
import { MuiOtpInput } from 'mui-one-time-password-input'
import DocumentIcon from '../assets/icons/document-icon.svg'
import { getCustomerCompanyPockets } from '../api/customers.api'
import { checkTwoFactorAuth, requestAccessToken } from '../api/auth.api'
import { getPaymentPendingRoutingDetailsData } from '../api/app-data.api'
import { baseButtonBlueBg, textFieldWhiteBg } from '../theme/custom-styles'
import { AppStateContext, AuthContext, NotificationsContext, UserContext } from '../contexts'
import { returnPaymentByPaymentsSpecialist, approvePaymentByPaymentsSpecialist } from '../api/payments.api'
import {getAttachmentFilePreSignedURL, getDownloadAttachmentFilePreSignedURL, uploadAttachmentFileToS3, resolveDocumentsAccessToken } from '../api/documents.api'
import { PaymentPendingRoutingDetailsTable } from '../components/payment-pending-routing-details-management/payment-pending-routing-details-table'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

const PaymentPendingRoutingDetailsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { paymentId, customerCompanyId } = useParams()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const currentPaymentDataRef = useRef<Payment | null>(null)
  const customerCompanyPocketsRef = useRef<Array<any>>([])
  const { updateLoadingStatus } = useContext(AppStateContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['customer-company-details-management', 'payments-to-review-management'])

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [decisionToTake, setDecisionToTake] = useState('')
  const [routingWarning, setRoutingWarning] = useState('')
  const [paymentActionReason, setPaymentActionReason] = useState('')
  const [paymentData, setPaymentData] = useState<Payment | null>(null)
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [paymentAttachment, setPaymentAttachment] = useState<any | null>(null)
  const [paymentCustomerData, setPaymentCustomerData] = useState<Customer | null>(null)
  const [openSetPaymentStatusConfimationModal, setOpenSetPaymentStatusConfimationModal] = useState(false)
  const [paymentStatusConfimationModalIsLoading, setPaymentStatusConfimationModalIsLoading] = useState(false)
  const [dialogSetReturnPaymentStatusIsOpen, setDialogSetReturnPaymentStatusIsOpen] = useState<boolean>(false)
  const [dialogSetApprovePaymentStatusIsOpen, setDialogSetApprovePaymentStatusIsOpen] = useState<boolean>(false)
  const [dialogSetReturnPaymentStatusIsLoading /* setDialogSetReturnPaymentStatusIsLoading */] = useState<boolean>(false)
  const [dialogSetApprovePaymentStatusIsLoading /* setDialogSetApprovePaymentStatusIsLoading */] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    if (!paymentId) return

    if (!customerCompanyId) return

    appDataSocket.on(`paymentPendingRouting:${paymentId}`, async (dataFromSocket: any) => {
      if (dataFromSocket.deleted === true) {
        navigate(`/payments-pending-routing/${customerCompanyId}`)
        return
      }

      handleGetPaymentPendingRoutingDetails()
    })

    appDataSocket.on(`paymentPendingRoutingDeleted:${paymentId}`, async () => {
      navigate(`/payments-pending-routing/${customerCompanyId}`)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, paymentId, customerCompanyId, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    if (!userSession) {
      return
    }

    if (userSession && userSession?.role !== 'super-admin') {
      navigate('/404')
      return
    }

    handleGetPaymentPendingRoutingDetails()
    handleGetCustomerCompanyPockets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  const handleGetPaymentPendingRoutingDetails = async () => {
    let localPaymentPendingRoutingToSet: any = {}
    const localPaymentPendingRouting = window.sessionStorage.getItem(`paymentPendingRoutingData:${paymentId}`)

    if (localPaymentPendingRouting) {
      localPaymentPendingRoutingToSet = JSON.parse(localPaymentPendingRouting)
      handleSetInitialData(localPaymentPendingRoutingToSet)
    }

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentPendingRoutingDetailsData(String(paymentId), accessToken)

      if (Object.keys(data).length === 0) {
        navigate(`/payments-pending-routing/${customerCompanyId}`)
        return
      }

      handleSetInitialData(data)
      window.sessionStorage.setItem(`paymentPendingRoutingData:${paymentId}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetPaymentPendingRoutingDetails > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const paymentCurrency = dataToSet.paymentPendingRouting.currency ? dataToSet.paymentPendingRouting.currency : 'cop'

    if (paymentCurrency !== selectedCurrency) {
      navigate(`/payments-pending-routing/${customerCompanyId}`)
      return
    }

    setPaymentCustomerData(dataToSet.customerData)
    currentPaymentDataRef.current = dataToSet.paymentPendingRouting
    setPaymentData(dataToSet.paymentPendingRouting)
    setLoadingLocalData(false)
  }

  const handleGetCustomerCompanyPockets = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))
      const {
        data: { customerCompanyPockets }
      } = await getCustomerCompanyPockets(String(customerCompanyId), 'cop', accessToken)

      customerCompanyPocketsRef.current = customerCompanyPockets
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleApprovePayment = () => {
    setErrors({})

    if (!paymentActionReason) {
      setErrors({
        paymentActionReason: t('confirmSetBackgroundResearchReasonModal.errors.emptyField', { ns: 'payments-to-review-management' })
      })
      return
    }

    if (!paymentAttachment) {
      setErrors({
        attachmentFileError: t('confirmSetBackgroundResearchReasonModal.errors.attachmentFileError', { ns: 'payments-to-review-management' })
      })
      return
    }

    setOpenSetPaymentStatusConfimationModal(true)
  }

  const handleApprovePaymentConfirmed = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setPaymentStatusConfimationModalIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
      return
    }

    try {
      setOtpCode('')
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')

      const paymentRoutingDataToSet = {
        paymentActionReason,
        voucher: {
          systemFilename: '',
          originalFilename: ''
        }
      }

      if (paymentAttachment) {
        const attachmentFileData = {
          attachmentFileType: paymentAttachment.fileType,
          attachmentFileSystemName: paymentAttachment.systemFilename
        }

        paymentRoutingDataToSet.voucher = {
          systemFilename: paymentAttachment.systemFilename,
          originalFilename: paymentAttachment.originalFilename
        }

        const attachmentFileToUploadPreSignedURL = await getAttachmentFilePreSignedURL(attachmentFileData, accessToken)
        await uploadAttachmentFileToS3(attachmentFileToUploadPreSignedURL.data, paymentAttachment.fileType, paymentAttachment.file as File)
      }

      await approvePaymentByPaymentsSpecialist(String(paymentId), paymentRoutingDataToSet, accessToken)

      setDecisionToTake('')
      setPaymentAttachment(null)
      setPaymentActionReason('')
      setDialogSetApprovePaymentStatusIsOpen(false)
      setOpenSetPaymentStatusConfimationModal(false)
      setPaymentStatusConfimationModalIsLoading(false)

      updateNewNotificationStatus?.(true, 'payment-status-updated-success')
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
      return
    }
  }

  const handleReturnPayment = () => {
    setErrors({})

    if (!paymentActionReason) {
      setErrors({
        paymentActionReason: t('confirmSetBackgroundResearchReasonModal.errors.emptyField', { ns: 'payments-to-review-management' })
      })
      return
    }

    setOpenSetPaymentStatusConfimationModal(true)
  }

  const handleReturnPaymentConfirmed = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setPaymentStatusConfimationModalIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
      return
    }

    try {
      setOtpCode('')
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')

      const paymentRoutingDataToSet = {
        paymentActionReason,
        voucher: {
          systemFilename: '',
          originalFilename: ''
        }
      }

      if (paymentAttachment) {
        const attachmentFileData = {
          attachmentFileType: paymentAttachment.fileType,
          attachmentFileSystemName: paymentAttachment.systemFilename
        }

        paymentRoutingDataToSet.voucher = {
          systemFilename: paymentAttachment.systemFilename,
          originalFilename: paymentAttachment.originalFilename
        }

        const attachmentFileToUploadPreSignedURL = await getAttachmentFilePreSignedURL(attachmentFileData, accessToken)
        await uploadAttachmentFileToS3(attachmentFileToUploadPreSignedURL.data, paymentAttachment.fileType, paymentAttachment.file as File)
      }

      await returnPaymentByPaymentsSpecialist(String(paymentId), paymentRoutingDataToSet, accessToken)

      setDecisionToTake('')
      setPaymentAttachment(null)
      setPaymentActionReason('')
      setDialogSetReturnPaymentStatusIsOpen(false)
      setOpenSetPaymentStatusConfimationModal(false)
      setPaymentStatusConfimationModalIsLoading(false)

      updateNewNotificationStatus?.(true, 'payment-status-updated-success')
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setPaymentStatusConfimationModalIsLoading(false)
      return
    }
  }

  const handleGetFileExtension = (filename: string) => {
    const index = filename.lastIndexOf('.')
    return index !== -1 ? filename.slice(index + 1) : ''
  }

  const handleAddAttachmentToPaymentDecision = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors({})

    if (event.target.files?.[0]) {
      const fileType = event.target.files[0].type
      const fileSize = event.target.files[0].size / 1024 / 1024

      if (fileSize > 5) {
        setErrors({
          attachmentFileError: t('complianceProcess.errors.attachmentFileError', { ns: 'payments-to-review-management' })
        })

        return
      }

      if (fileType !== 'application/pdf' && !fileType.startsWith('image/')) {
        setErrors({
          attachmentFileError: t('complianceProcess.errors.attachmentFileTypeError', { ns: 'payments-to-review-management' })
        })

        return
      }

      const reader = new FileReader()
      reader.readAsDataURL(event.target.files[0])
      reader.onload = () => {
        setPaymentAttachment({
          fileType,
          base64: reader.result as string,
          file: event.target.files ? event.target.files[0] : null,
          originalFilename: event.target.files ? event.target.files[0].name : '',
          systemFilename: `payment-decision-${new Date().getTime()}.${handleGetFileExtension(String(event.target.files?.[0].name))}`
        })

        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
      reader.onerror = function (error) {
        console.log('Error: ', error)
      }
    }
  }

  const handleDownloadAttachmentFile = async (attachmentFilename: string) => {
    setErrors({})

    try {
      updateLoadingStatus?.(true)
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const downloadAttachmentFilePreSignedURL = await getDownloadAttachmentFilePreSignedURL({ attachmentFilename }, accessToken)

      window.open(downloadAttachmentFilePreSignedURL.data, '_blank')
      setTimeout(() => updateLoadingStatus?.(false), 1000)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      updateLoadingStatus?.(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate('/customer-companies')}
        >
          <Typography
            key="1"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        </ListItemButton>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/customer-company-details/${customerCompanyId}`)}
        >
          <Typography
            key="2"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.secondLevel')}
          </Typography>
        </ListItemButton>
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => navigate(`/payments-pending-routing/${customerCompanyId}`)}
        >
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.pendingRouting')}
          </Typography>
        </ListItemButton>
      </Breadcrumbs>
      {loadingLocalData ? (
        <div className="payment-to-review-details-container">
          <div className="payment-to-review-details-title">{t('title', { ns: 'payments-to-review-management' })}</div>
          <div className="documents-list-container">
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
          </div>
        </div>
      ) : (
        <>
          <div className="payment-to-review-details-container">
            <div className="payment-to-review-details-title">{t('title', { ns: 'payments-to-review-management' })}</div>
            <PaymentPendingRoutingDetailsTable
              paymentData={paymentData}
              setRoutingWarning={setRoutingWarning}
              paymentCustomerData={paymentCustomerData}
              additionalInfo={customerCompanyPocketsRef.current}
              handleDownloadAttachmentFile={handleDownloadAttachmentFile}
              handleReturnPayment={() => {
                setDecisionToTake('return')
                setDialogSetReturnPaymentStatusIsOpen(true)
              }}
              handleApprovePayment={() => {
                setDecisionToTake('approve')
                setDialogSetApprovePaymentStatusIsOpen(true)
              }}
            />
          </div>
        </>
      )}
      {dialogSetApprovePaymentStatusIsOpen && (
        <Dialog
          errors={errors}
          actionButtonSuccessStyle
          handleDialogAction={handleApprovePayment}
          dialogIsLoading={dialogSetApprovePaymentStatusIsLoading}
          actionButtonText={t('approvePaymentDecisionModal.actionButtonText', { ns: 'payments-to-review-management' })}
          dialogTitle={t('approvePaymentDecisionModal.title', { ns: 'payments-to-review-management' })}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setDecisionToTake('')
              setPaymentAttachment(null)
              setPaymentActionReason('')
            }

            setDialogSetApprovePaymentStatusIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <TextField
              fullWidth
              multiline
              variant="standard"
              sx={textFieldWhiteBg}
              id="filled-textarea"
              value={paymentActionReason || ''}
              error={!!errors.paymentActionReason}
              helperText={errors.paymentActionReason}
              onChange={(event) => {
                setErrors({})
                setPaymentActionReason(event.target.value.toUpperCase())
              }}
              label={t('returnPaymentDecisionModal.placeholder', { ns: 'payments-to-review-management' })}
            />
            {routingWarning && <div className="payment-routing-warning">{routingWarning}</div>}
            {errors?.attachmentFileError && (
              <div
                style={{ marginTop: '20px' }}
                className="error-message"
              >
                {errors?.attachmentFileError}
              </div>
            )}
            {paymentAttachment ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <img
                    src={DocumentIcon}
                    alt="document-icon"
                  />
                  <div style={{ color: '#00e', textDecorationLine: 'underline' }}>{paymentAttachment.originalFilename}</div>
                </div>
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => setPaymentAttachment(null)}
                >
                  <img
                    src={TrashIcon}
                    alt="trash-icon"
                  />
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '20px' }}>
                <Button
                  fullWidth
                  variant="text"
                  component="label"
                  sx={baseButtonBlueBg}
                >
                  {t('complianceProcess.attachFileToResponse', { ns: 'payments-to-review-management' })}
                  <VisuallyHiddenInput
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,application/pdf"
                    onChange={handleAddAttachmentToPaymentDecision}
                  />
                </Button>
              </div>
            )}
          </div>
        </Dialog>
      )}
      {dialogSetReturnPaymentStatusIsOpen && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={handleReturnPayment}
          dialogIsLoading={dialogSetReturnPaymentStatusIsLoading}
          actionButtonText={t('returnPaymentDecisionModal.actionButtonText', { ns: 'payments-to-review-management' })}
          dialogTitle={t('returnPaymentDecisionModal.title', { ns: 'payments-to-review-management' })}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setDecisionToTake('')
              setPaymentAttachment(null)
              setPaymentActionReason('')
            }

            setDialogSetReturnPaymentStatusIsOpen(openState)
          }}
        >
          <div className="dialog-content-text">
            <TextField
              fullWidth
              multiline
              variant="standard"
              sx={textFieldWhiteBg}
              id="filled-textarea"
              value={paymentActionReason || ''}
              error={!!errors.paymentActionReason}
              helperText={errors.paymentActionReason}
              onChange={(event) => {
                setErrors({})
                setPaymentActionReason(event.target.value.toUpperCase())
              }}
              label={t('returnPaymentDecisionModal.placeholder', { ns: 'payments-to-review-management' })}
            />
            {routingWarning && <div className="payment-routing-warning">{routingWarning}</div>}
            {errors?.attachmentFileError && (
              <div
                style={{ marginTop: '20px' }}
                className="error-message"
              >
                {errors?.attachmentFileError}
              </div>
            )}
            {paymentAttachment ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <img
                    src={DocumentIcon}
                    alt="document-icon"
                  />
                  <div style={{ color: '#00e', textDecorationLine: 'underline' }}>{paymentAttachment.originalFilename}</div>
                </div>
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={() => setPaymentAttachment(null)}
                >
                  <img
                    src={TrashIcon}
                    alt="trash-icon"
                  />
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '20px' }}>
                <Button
                  fullWidth
                  variant="text"
                  component="label"
                  sx={baseButtonBlueBg}
                >
                  {t('complianceProcess.attachFileToResponse', { ns: 'payments-to-review-management' })}
                  <VisuallyHiddenInput
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,application/pdf"
                    onChange={handleAddAttachmentToPaymentDecision}
                  />
                </Button>
              </div>
            )}
          </div>
        </Dialog>
      )}
      {openSetPaymentStatusConfimationModal && (
        <Dialog
          errors={errors}
          actionButtonWarningStyle
          handleDialogAction={() => {
            if (decisionToTake === 'return') {
              handleReturnPaymentConfirmed()
              return
            }

            handleApprovePaymentConfirmed()
          }}
          handleDialogOpenState={(openState: boolean) => {
            if (openState === false) {
              setErrors({})
              setOtpCode('')
            }

            setOpenSetPaymentStatusConfimationModal(openState)
          }}
          dialogIsLoading={paymentStatusConfimationModalIsLoading}
          dialogTitle={t('confirmSendPaymentModal.title', { ns: 'payments-to-review-management' })}
          actionButtonText={t('confirmSendPaymentModal.actionButtonText', { ns: 'payments-to-review-management' })}
        >
          <div
            className="step"
            style={{ marginTop: '0px', gap: '16px' }}
          >
            <div
              className="step-instruction-container"
              style={{ fontSize: '14px', color: '#F59E0B' }}
            >
              {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
            </div>
            <MuiOtpInput
              length={6}
              value={otpCode}
              onChange={(newValue: string) => setOtpCode(newValue)}
              className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
            />
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default PaymentPendingRoutingDetailsManagement
